import React from 'react';
import { useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import styled from 'styled-components';

import Animation from '@components/Animation';
import FAQ from '@components/FAQ';
import Header from '@components/Header';
import ListsPage from '@components/ListsPage';
import UserProfile from '@components/UserProfile';
import useAPI from '@hooks/useAPI';
import useFetchWithKey from '@hooks/useFetchWithKey';
import Footer from '@shared/Footer';
import { Grid } from '@shared/Grid';
import SignOut from '@shared/SignOut';
import Spinner from '@shared/Spinner';
import Home from '@src/components/Console/Main';
import urls from '@src/urls';

export default function Console() {
  const keyData = useAPI('get', 'ScreenConsoleKeyApi', `key`);
  const keyFromState = useSelector(state => state.keys.key);
  const apiKey = keyFromState || (keyData.response && keyData.response.key);
  const accountNotAurthorized = keyData.error && keyData.error.includes('401');
  const hasAuthError = keyData.error;
  const [hasLongWait, setHasLongWait] = React.useState(false);
  React.useEffect(() => {
    const interval = setTimeout(() => {
      setHasLongWait(true);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const showSignOut = accountNotAurthorized || (hasLongWait && !apiKey);

  if (accountNotAurthorized) {
    return (
      <PageContainer gutter>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>
        <MainContainer>
          <ErrorContainer>
            This account has not been authorized. Please contact{' '}
            <a href='mailto:screen-support@enigma.com'>
              screen-support@enigma.com
            </a>{' '}
            for help.
          </ErrorContainer>
          <Footer />
        </MainContainer>
      </PageContainer>
    );
  }

  if (hasAuthError) {
    return (
      <PageContainer gutter>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>
        <MainContainer>
          <ErrorContainer>
            <div>Session expired. Please sign out and try again. </div>
            <LogoutContainer>
              <SignOut />
            </LogoutContainer>
          </ErrorContainer>
          <Footer />
        </MainContainer>
      </PageContainer>
    );
  }
  const hasApiKey = !!apiKey;
  return (
    <PageContainer gutter>
      <HeaderWrapper>
        <Header />
      </HeaderWrapper>
      <MainContainer>
        {!apiKey && (
          <SpinnerContainer>
            <Spinner center />
          </SpinnerContainer>
        )}
        {apiKey && (
          <>
            <Animation in name='slide'>
              <Switch>
                <Route exact path={[urls.root(), urls.authed.console()]}>
                  <Home />
                </Route>
                <Route exact path={urls.authed.lists()}>
                  <ListsPage />
                </Route>
                <Route exact path={urls.authed.profile()}>
                  <UserProfile />
                </Route>
                <Route exact path={urls.authed.faq()}>
                  <FAQ />
                </Route>
              </Switch>
            </Animation>
          </>
        )}
        {showSignOut && (
          <div>
            <SignOut />
          </div>
        )}
        <Footer />
      </MainContainer>
    </PageContainer>
  );
}

const SpinnerContainer = styled.div`
  height: 80vh;
`;

const LogoutContainer = styled.div`
  max-width: 300px;
  margin: 10px auto;
  text-align: center;
`;

const HeaderWrapper = styled.div`
  justify-content: flex-start;
`;

const PageContainer = styled(Grid)`
  max-width: 1440px;
  width: 100vw;
`;

const MainContainer = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 1080px;
`;

const ErrorContainer = styled.div`
  text-align: center;
  height: 40vh;
`;
